/**
 * Is the quote worksheet on for this viewer? (#6274 split; Q9 cutover.) One read of the organization's switches,
 * organizations/{orgId}/settings/quoteWorksheet (employees of the org may read settings; nothing in the client writes
 * them):
 *
 *   live                 exactly true: the worksheet is launched. Every Create Quote opens it, for everyone with
 *                        Quoting access. The SAME field the worksheet callables check (assertWorksheetOpenTo in
 *                        functions/src/quoting/services/quoteV2Common.ts), so the screens and the server agree.
 *   worksheetForManagers exactly true: before launch, managers (Owner, Admin, Manager) get the worksheet so it can be
 *                        checked in production. Everyone else keeps today's screens. Nobody has set it: turning it on
 *                        is Nick's release of the worksheet to managers (DR-O87-2, #8450).
 *   previewForManagers   exactly true: the PRICE-CATALOG preview (Nick, 2026-10-12: the price catalog, pricing and
 *                        customer quote wording screens for managers). It never opens the worksheet: it has been on in
 *                        production since 2026-10-12, so reusing it would have released the worksheet with the deploy.
 *
 * `worksheet` = live OR worksheet preview; `catalog` (the price-catalog screens) = live OR either preview. Anything else, a
 * missing document, or a read that fails is off, a failed RE-read included (a cached true is not kept): today's screens,
 * which stay working throughout (setting live back to false is the rollback). Removed with the old screens (Q10).
 *
 * VISIBILITY ONLY. What a user may do is enforced by the server callables (module and role checks).
 */
import { useQuery } from '@tanstack/react-query';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { COLLECTIONS } from '../../config/collections';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganization } from '../../contexts/OrganizationContext';

export const QUOTE_WORKSHEET_SETTINGS_DOC_ID = 'quoteWorksheet';

export const quoteWorksheetSwitchKeys = {
  all: ['quoteWorksheetSwitch'] as const,
  detail: (orgId: string) => [...quoteWorksheetSwitchKeys.all, orgId] as const,
};

/** Launched only when the settings document says exactly `live: true` (the server's own test). */
export function isLiveSwitchOn(settings: Record<string, unknown> | undefined): boolean {
  return settings?.live === true;
}

/** The managers' price-catalog preview is on only when the settings document says exactly `previewForManagers: true`. */
export function isPreviewSwitchOn(settings: Record<string, unknown> | undefined): boolean {
  return settings?.previewForManagers === true;
}

/** The managers' worksheet preview is on only when the settings document says exactly `worksheetForManagers: true`. */
export function isWorksheetPreviewOn(settings: Record<string, unknown> | undefined): boolean {
  return settings?.worksheetForManagers === true;
}

interface Switches {
  live: boolean;
  previewForManagers: boolean;
  worksheetForManagers: boolean;
}

async function fetchSwitches(orgId: string): Promise<Switches> {
  const snap = await getDoc(doc(db, COLLECTIONS.ORGANIZATIONS, orgId, 'settings', QUOTE_WORKSHEET_SETTINGS_DOC_ID));
  const data = snap.exists() ? (snap.data() as Record<string, unknown>) : undefined;
  return { live: isLiveSwitchOn(data), previewForManagers: isPreviewSwitchOn(data), worksheetForManagers: isWorksheetPreviewOn(data) };
}

export interface QuoteWorksheetSwitch {
  /** The organization has launched the worksheet. */
  live: boolean;
  /** A manager, with the worksheet preview switch (worksheetForManagers) on. */
  preview: boolean;
  /** Use the worksheet: live OR preview, and the last read did not fail. */
  worksheet: boolean;
  /** Show the price-catalog screens: live OR a manager with either preview switch on, and the last read did not fail. */
  catalog: boolean;
  /** Not decided yet: show neither the old screen nor the worksheet. */
  isLoading: boolean;
  /** The answer above is a stale cached one and a fresh read is on its way: a page deciding now must wait for it. */
  isRefreshing: boolean;
  /** The last read failed (any answer above is an older cached one). */
  readFailed: boolean;
  /** The cached answer is older than the short stale time (never, for a viewer the switch is not read for). */
  isStale: boolean;
  /** A read of the switch is on its way. */
  isFetching: boolean;
  /** When the last read failed (ms since epoch; 0 if none has). */
  failedAt: number;
  /** Read the switch again now. */
  refetch: () => Promise<unknown>;
}

export function useQuoteWorksheetSwitch({ enabled = true }: { enabled?: boolean } = {}): QuoteWorksheetSwitch {
  const { user, isManagerOrAdmin } = useAuth();
  const { currentOrganization, loading: orgLoading } = useOrganization();
  const orgId = currentOrganization?.organizationId ?? '';
  // An External account may not read settings (firestore.rules isEmployee) and cannot open Quoting pages: always off.
  const asked = enabled && user?.isExternal !== true;
  const query = useQuery({
    queryKey: quoteWorksheetSwitchKeys.detail(orgId),
    queryFn: () => fetchSwitches(orgId),
    enabled: asked && orgId !== '',
    // Short, and re-read when the tab comes back into focus (the app default is off): a rollback (live back to false)
    // must reach open tabs before their next Create Quote / View Quote, not five minutes later. A page already open is
    // not moved by a later read (useWorksheetDecision keeps its first decision); the server refuses what it must.
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });
  // Still loading until the organization is known AND its switches have been read: a page opened by address on a fresh
  // load must wait, not decide "off" early. "Not loading, no organization" is NOT an answer: the context reads that for
  // one render after sign-in, before its load starts. A load that really ends with no organization is OrgBoundary's to
  // show (the route switches sit inside it). A failed read is not pending: it settles as off.
  const isLoading = asked && (orgLoading || orgId === '' || query.isPending);
  const live = asked && query.data?.live === true;
  const manager = asked && isManagerOrAdmin();
  const preview = manager && query.data?.worksheetForManagers === true;
  const catalogPreview = manager && query.data?.previewForManagers === true;
  // A failed read is off even with an older cached answer (SECURITY L1, #8450): after a rollback, a network error must not
  // keep the cached true in force.
  const readFailed = asked && query.isError;
  const isRefreshing = asked && query.data !== undefined && query.isStale && query.isFetching;
  return {
    live,
    preview,
    worksheet: (live || preview) && !readFailed,
    catalog: (live || preview || catalogPreview) && !readFailed,
    isLoading,
    isRefreshing,
    readFailed,
    isStale: asked && query.isStale,
    isFetching: asked && query.isFetching,
    failedAt: query.errorUpdatedAt,
    refetch: query.refetch,
  };
}
